import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  createReferralCampaign,
  updateReferralCampaign,
  getAllReferralCampaigns,
  getReferralTerms,
  generateReferralTerms,
  saveReferralTermsDraft,
  discardReferralTermsDraft,
  restoreReferralTermsDraft,
  publishReferralTerms,
} from "./referralCampaignAPI";

export const referralCampaignSlice = createSlice({
  name: "referralCampaign",
  initialState: {
    referralCampaigns: [],
    status: "idle",
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "startDate",
    selectedId: null,
    selectedCampaign: null,
    addModalOpen: false,
    editModalOpen: false,
    termsModalOpen: false,
    terms: null,
    termsDraft: "",
    termsPrompt: "",
    termsGenerating: false,
    successMessage: "",
    errorMessage: "",
  },
  reducers: {
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    setSelectedCampaign: (state, action) => {
      state.selectedCampaign = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setReferralCampaigns: (state, action) => {
      state.referralCampaigns = action.payload;
      state.status = "success";
    },
    showAddModal: (state, action) => {
      state.addModalOpen = action.payload;
    },
    showEditModal: (state, action) => {
      state.editModalOpen = action.payload;
    },
    showTermsModal: (state, action) => {
      state.termsModalOpen = action.payload;
      if (!action.payload) {
        state.terms = null;
        state.termsDraft = "";
        state.termsPrompt = "";
      }
    },
    setTerms: (state, action) => {
      const terms = action.payload;
      state.terms = terms;
      // The draft box always opens on something editable: the draft if there is one, otherwise
      // the live wording, so publishing a small change never means retyping the whole thing.
      state.termsDraft = terms?.draftMarkdown ?? terms?.publishedMarkdown ?? "";
      state.termsPrompt = terms?.prompt ?? "";
    },
    setTermsDraft: (state, action) => {
      state.termsDraft = action.payload;
    },
    setTermsPrompt: (state, action) => {
      state.termsPrompt = action.payload;
    },
    setTermsGenerating: (state, action) => {
      state.termsGenerating = action.payload;
    },
    viewClaims: (state, action) => {
      window.location.href = "referral-campaigns/" + state.selectedId;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setSelectedId,
  setSelectedCampaign,
  setPage,
  setRowsPerPage,
  setPagination,
  setReferralCampaigns,
  showAddModal,
  showEditModal,
  showTermsModal,
  setTerms,
  setTermsDraft,
  setTermsPrompt,
  setTermsGenerating,
  viewClaims,
  setSuccessMessage,
  setErrorMessage,
} = referralCampaignSlice.actions;

export const selectState = (state) => state.referralCampaign;

export const handleGetReferralCampaigns = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const response = await getAllReferralCampaigns(
      state.referralCampaign.page,
      state.referralCampaign.rowsPerPage,
      state.referralCampaign.sort,
      state.referralCampaign.sortBy
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setReferralCampaigns(content));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleAddReferralCampaign =
  (referralCampaign) => async (dispatch, getState) => {
    dispatch(showLoading());

    try {
      await createReferralCampaign(referralCampaign);
      dispatch(showAddModal(false));
      dispatch(setSuccessMessage("Successfully added referral campaign!"));
      dispatch(handleGetReferralCampaigns());
    } catch (error) {
      dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleUpdateReferralCampaign =
  (referralCampaign) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await updateReferralCampaign(
        state.referralCampaign.selectedId,
        referralCampaign
      );
      dispatch(showEditModal(false));
      dispatch(setSuccessMessage("Successfully updated referral campaign!"));
      dispatch(handleGetReferralCampaigns());
      dispatch(setSelectedId(null));
      dispatch(setSelectedCampaign(null));
    } catch (error) {
      dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleGetReferralTerms = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    const response = await getReferralTerms(id);
    dispatch(setTerms(response.data));
  } catch (error) {
    dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
  } finally {
    dispatch(hideLoading());
  }
};

// Kept out of the loading bar: drafting takes long enough that the admin needs a control that
// stays disabled for the whole call, not a bar that finishes first.
export const handleGenerateReferralTerms = (id, prompt) => async (dispatch) => {
  dispatch(setTermsGenerating(true));

  try {
    const response = await generateReferralTerms(id, prompt);
    dispatch(setTerms(response.data));
    dispatch(setSuccessMessage("Draft ready. Read it before publishing."));
  } catch (error) {
    dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
  } finally {
    dispatch(setTermsGenerating(false));
  }
};

export const handleSaveReferralTermsDraft =
  (id, markdown, prompt) => async (dispatch) => {
    dispatch(showLoading());

    try {
      const response = await saveReferralTermsDraft(id, markdown, prompt);
      dispatch(setTerms(response.data));
      dispatch(setSuccessMessage("Draft saved. Nothing has changed for users yet."));
    } catch (error) {
      dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleDiscardReferralTermsDraft = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    const response = await discardReferralTermsDraft(id);
    dispatch(setTerms(response.data));
    dispatch(setSuccessMessage("Draft discarded."));
  } catch (error) {
    dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleRestoreReferralTermsDraft = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    const response = await restoreReferralTermsDraft(id);
    dispatch(setTerms(response.data));
  } catch (error) {
    dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const handlePublishReferralTerms = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    const response = await publishReferralTerms(id);
    dispatch(setTerms(response.data));
    dispatch(setSuccessMessage("Terms published. Users see this wording now."));
    dispatch(handleGetReferralCampaigns());
  } catch (error) {
    dispatch(setErrorMessage(referralCampaignErrorMessage(error)));
  } finally {
    dispatch(hideLoading());
  }
};

// The API rejects overlapping active campaigns and bad date ranges with a message
// that the admin needs to see, rather than a generic axios status text.
const referralCampaignErrorMessage = (error) =>
  error?.response?.data?.message || error.message;

export default referralCampaignSlice.reducer;
